/**
 * Voucher Composer V2 · Canonical Renderer.
 *
 * ONE component renders V2 designs in BOTH admin (edit) and guest
 * wallet (read). Read mode ships zero editor code paths — selection,
 * drag handles, alignment guides only mount when `mode === 'edit'`.
 *
 * Coordinate system:
 *   All element {x, y, width, height} values are NORMALISED to the
 *   canvas (0.0 – 1.0). Sizes/typography use container-query units
 *   (`cqw`) so the exact same composition renders identically at any
 *   physical size and on any device.
 *
 * Selection / manipulation (edit mode only):
 *   • Click any element to select
 *   • Drag body → move
 *   • Drag any of the 8 handles → resize
 *   • Drag the top rotate knob → rotate
 *   • Snap guides visualise centre & edges when within 1 % of alignment
 *   • Functional elements (qr / validity / terms_cta / code / brand /
 *     title / value) cannot be deleted; they enforce minimum readable
 *     sizes.
 */

import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import * as LucideAll from 'lucide-react';
import { formatBenefitValue } from './benefitFormatter';

// Phase 2A · Bindings still exist as first-class identities so the
// value / title / QR layers can bind live voucher data, but they are
// no longer FORCED onto every canvas. Admin can add, remove, move,
// duplicate any of them. The renderer treats them as regular elements.
export const FUNCTIONAL_IDS = new Set(['brand', 'logo', 'title', 'subtitle',
  'value', 'qr', 'code', 'validity', 'terms_cta']);
// Phase 2A · NO undeletable elements. Business data lives on the
// voucher record; the visual composition is entirely admin-controlled.
export const UNDELETABLE_IDS = new Set();
const MIN_QR_WIDTH = 0.08;
const MIN_ELEMENT_WIDTH = 0.02;
const MIN_ELEMENT_HEIGHT = 0.01;

// ── Value formatter for the `value` binding ─────────────────────
// Uses the CANONICAL benefitFormatter so a percentage never renders
// with a ₹ prefix and a complimentary item never gets forced into a
// currency slot.
function formatValue(v) {
  if (!v) return '';
  const bt = v?.benefit_type;
  const rawName = v?.name || v?.face_value_label || '';
  // For discount types we render the OFF suffix; for cashback the
  // BACK suffix; for complimentary items we surface the configured
  // free-text name; for custom we render whatever the admin typed.
  if (bt === 'complimentary_item')       return rawName || 'Complimentary';
  if (bt === 'free_service')             return rawName || 'FREE SERVICE';
  if (bt === 'custom')                   return rawName || (v.benefit_value ? String(v.benefit_value) : '—');
  const variant = (bt === 'cashback') ? 'back'
                 : (bt === 'bogo' || bt === 'buy_one_get_one') ? 'value'
                 : 'off';
  const out = formatBenefitValue(v, variant);
  if (bt === 'bogo' || bt === 'buy_one_get_one') return 'BUY 1 GET 1';
  return out || rawName || '—';
}

// Resolve the render text/data for a functional element.
function resolveContent(el, ctx) {
  const { voucher, outlet } = ctx;
  switch (el.type) {
    case 'brand':      return outlet?.name || '—';
    case 'title':      return voucher?.name || voucher?.title || 'Voucher';
    case 'subtitle':   return voucher?.subtitle || voucher?.description || (outlet?.tagline || '');
    case 'value':      return formatValue(voucher || {});
    // Phase 2B · plaintext voucher_code is ONLY exposed when the
    // caller passes `ctx.revealed = true` AND `ctx.revealedCode`.
    // Otherwise we render the pre-existing masked value (or a
    // decorative placeholder if nothing at all is present).
    case 'code':       return (ctx.revealed && ctx.revealedCode)
                              ? ctx.revealedCode
                              : (voucher?.voucher_code_masked || voucher?.code || '••••••');
    case 'validity':   return ctx.validUntil ? `Valid until ${ctx.validUntil}` : '';
    case 'terms_cta':  return el.content?.text || 'Terms & Conditions';
    default:           return el.content?.text || '';
  }
}

// Build CSS style from an element for read-mode rendering.
function elementBoxStyle(el, canvas) {
  const rot = el.rotation || 0;
  return {
    position: 'absolute',
    left:     `${el.x * 100}%`,
    top:      `${el.y * 100}%`,
    width:    `${el.width * 100}%`,
    height:   `${el.height * 100}%`,
    transform: rot ? `rotate(${rot}deg)` : undefined,
    transformOrigin: 'center',
    opacity: el.opacity ?? 1,
    zIndex: el.z_index ?? 5,
    // when opacity/hidden === 0 we still render but invisible so hit-tests
    // remain deterministic in edit mode. Read mode: skip hidden elements.
  };
}

// Body font-size expressed as fraction of canvas WIDTH. `size` in the
// element style is stored as fraction (0-1 of container width).
const textStyleFor = (el) => {
  const s = el.style || {};
  return {
    fontFamily: s.font || 'Inter',
    fontWeight: s.weight || 500,
    fontSize:   `${(s.size || 0.028) * 100}cqw`,
    lineHeight: s.line_height ?? 1.1,
    color:      s.color || '#F7F5EE',
    letterSpacing: s.letter_spacing || 'normal',
    textAlign:  s.align || 'center',
    textTransform: s.text_transform || 'none',
    textDecoration: s.text_decoration || 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      s.align === 'left' ? 'flex-start' :
      s.align === 'right' ? 'flex-end' : 'center',
    padding: 0,
    margin:  0,
    overflow: 'hidden',
    textShadow: s.text_shadow || undefined,
  };
};

// ── Individual element renderers (pure) ────────────────────────
function ElementText({ el, text }) {
  return (
    <div style={{ width: '100%', height: '100%', ...textStyleFor(el) }}>
      <span style={{ display: 'block', width: '100%' }}>{text}</span>
    </div>
  );
}
function ElementLogo({ el, ctx }) {
  const url = ctx.outlet?.logo;
  const initials = ctx.outlet?.initials || (ctx.outlet?.name || 'RP').slice(0, 2).toUpperCase();
  const s = el.style || {};
  const fit = s.fit || 'contain';
  const radius = s.border_radius === 999 ? '50%' : `${s.border_radius || 0}px`;
  if (url) {
    return <img src={url} alt="" draggable={false}
                style={{ width: '100%', height: '100%', objectFit: fit,
                         borderRadius: radius, background: s.background || 'transparent' }} />;
  }
  return (
    <div style={{ width: '100%', height: '100%', borderRadius: radius,
                  background: s.background || 'rgba(255,255,255,0.08)',
                  color: s.color || '#F7F5EE', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontFamily: 'Montserrat', fontWeight: 800,
                  fontSize: '5.5cqw', letterSpacing: '0.06em' }}>
      {initials}
    </div>
  );
}
function ElementQR({ el, ctx }) {
  const s = el.style || {};
  // Phase 2B · The QR NEVER encodes the plaintext voucher code.
  // Before Reveal:  ctx.qrValue is empty     → tasteful placeholder.
  // After Reveal:   ctx.qrValue is the signed short-lived JWT payload
  //                 issued by /loyalty/guest/wallet/reveal-voucher.
  const value = ctx.qrValue || '';
  const padPct = (s.padding ?? 0.010) * 100;
  const radius = s.border_radius || 10;
  const revealed = !!value;
  if (!revealed) {
    // Design intentionally reserves QR real-estate. Show a locked
    // placeholder so the composition holds — but NO scannable code
    // is emitted into the DOM.
    return (
      <div data-testid="qr-locked"
           style={{ width: '100%', height: '100%', background: s.bg || '#F7F5EE',
                    borderRadius: `${radius}px`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    color: 'rgba(14,44,34,0.35)',
                    fontFamily: 'Inter', fontWeight: 700,
                    fontSize: '3.2cqw', letterSpacing: '0.20em',
                    textTransform: 'uppercase' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ fontSize: '5cqw' }}>◱</div>
          <div>Tap Reveal</div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ width: '100%', height: '100%', background: s.bg || '#F7F5EE',
                  borderRadius: `${radius}px`, padding: `${padPct}%`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
      <QRCodeSVG value={value} size={512}
              fgColor={s.fg || '#0E2C22'} bgColor={s.bg || '#F7F5EE'}
              includeMargin={false}
              style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
function ElementRect({ el }) {
  const s = el.style || {};
  return (
    <div style={{ width: '100%', height: '100%',
                  background: s.fill || 'rgba(255,255,255,0.1)',
                  borderRadius: `${s.border_radius || 0}px`,
                  border: s.stroke_width ? `${s.stroke_width}px solid ${s.stroke || 'transparent'}` : 'none' }} />
  );
}
function ElementCircle({ el }) {
  const s = el.style || {};
  return (
    <div style={{ width: '100%', height: '100%', borderRadius: '50%',
                  background: s.fill || 'rgba(255,255,255,0.1)',
                  border: s.stroke_width ? `${s.stroke_width}px solid ${s.stroke || 'transparent'}` : 'none' }} />
  );
}
function ElementLine({ el }) {
  const s = el.style || {};
  return (
    <div style={{ width: '100%', height: '100%',
                  borderTop: `${s.thickness || 1}px ${s.dash === 'dashed' ? 'dashed' :
                                                     s.dash === 'dotted' ? 'dotted' : 'solid'} ${s.color || '#C9A227'}`,
                  marginTop: 0 }} />
  );
}
function ElementImage({ el }) {
  const url = el.content?.url;
  const s = el.style || {};
  if (!url) {
    return (
      <div style={{ width: '100%', height: '100%',
                    background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 8px, rgba(255,255,255,0.10) 8px 16px)',
                    borderRadius: `${s.border_radius || 8}px`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.55)', fontSize: '2.5cqw' }}>
        Image
      </div>
    );
  }
  return <img src={url} alt="" draggable={false}
              style={{ width: '100%', height: '100%',
                       objectFit: s.fit || 'cover',
                       borderRadius: `${s.border_radius || 0}px` }} />;
}
function ElementChip({ el }) {
  const s = el.style || {};
  return (
    <div style={{ width: '100%', height: '100%',
                  background: s.bg || '#C9A227', color: s.color || '#0E2C22',
                  fontFamily: s.font || 'Inter', fontWeight: s.weight || 700,
                  fontSize: `${(s.size || 0.020) * 100}cqw`,
                  letterSpacing: s.letter_spacing || '0.20em',
                  textTransform: s.text_transform || 'uppercase',
                  borderRadius: `${s.border_radius || 999}px`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 2px 6px ${(s.bg || '#C9A227')}55, inset 0 0 0 1px rgba(255,255,255,0.28)` }}>
      {el.content?.text || 'CHIP'}
    </div>
  );
}

// ── Ribbons — SVG-based, resizable, recolourable ───────────────
function ElementRibbon({ el }) {
  const s = el.style || {};
  const fill      = s.fill      || '#C9A227';
  const fill2     = s.fill2     || null;      // optional secondary colour
  const strokeCol = s.stroke    || 'rgba(0,0,0,0.25)';
  const textCol   = s.color     || '#0E2C22';
  const text      = el.content?.text || 'FEATURED';
  const variant   = s.variant   || 'horizontal';
  const grad = fill2 ? (
    <defs>
      <linearGradient id={`rgrad_${el.id}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor={fill} />
        <stop offset="100%" stopColor={fill2} />
      </linearGradient>
    </defs>
  ) : null;
  const rfill = fill2 ? `url(#rgrad_${el.id})` : fill;

  if (variant === 'corner') {
    return (
      <svg viewBox="0 0 200 200" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <polygon points="0,0 200,0 200,200" fill={rfill} />
        <text x="130" y="60" transform="rotate(45 130 60)"
              fill={textCol} fontFamily={s.font || 'Inter'} fontWeight={s.weight || 800}
              fontSize="14" textAnchor="middle" letterSpacing="2">{text}</text>
      </svg>
    );
  }
  if (variant === 'banner') {
    return (
      <svg viewBox="0 0 300 90" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <path d="M0,10 L280,10 L300,45 L280,80 L0,80 Z" fill={rfill} stroke={strokeCol} />
        <text x="150" y="55" fill={textCol} fontFamily={s.font || 'Inter'}
              fontWeight={s.weight || 800} fontSize="26"
              textAnchor="middle" letterSpacing="4">{text}</text>
      </svg>
    );
  }
  if (variant === 'tail') {
    return (
      <svg viewBox="0 0 300 90" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <path d="M0,10 L300,10 L280,45 L300,80 L0,80 L20,45 Z" fill={rfill} stroke={strokeCol} />
        <text x="150" y="55" fill={textCol} fontFamily={s.font || 'Inter'}
              fontWeight={s.weight || 800} fontSize="26"
              textAnchor="middle" letterSpacing="4">{text}</text>
      </svg>
    );
  }
  if (variant === 'folded') {
    // A wide ribbon with folded ends (three-D depth)
    return (
      <svg viewBox="0 0 320 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <polygon points="0,30 20,10 300,10 320,30 300,50 320,90 0,90 20,50" fill={rfill} stroke={strokeCol} />
        <polygon points="0,30 20,50 20,10" fill="rgba(0,0,0,0.30)" />
        <polygon points="320,30 300,50 300,10" fill="rgba(0,0,0,0.30)" />
        <text x="160" y="60" fill={textCol} fontFamily={s.font || 'Inter'}
              fontWeight={s.weight || 800} fontSize="26"
              textAnchor="middle" letterSpacing="4">{text}</text>
      </svg>
    );
  }
  if (variant === 'festival') {
    return (
      <svg viewBox="0 0 300 120" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <path d="M20,0 L280,0 L260,50 L280,110 L20,110 L40,50 Z" fill={rfill} stroke={strokeCol} />
        <path d="M20,0 L40,50 L20,60 Z"    fill="rgba(0,0,0,0.28)" />
        <path d="M280,0 L260,50 L280,60 Z" fill="rgba(0,0,0,0.28)" />
        <text x="150" y="70" fill={textCol} fontFamily={s.font || 'Playfair Display'}
              fontWeight={s.weight || 800} fontSize="34"
              textAnchor="middle" letterSpacing="4">{text}</text>
      </svg>
    );
  }
  if (variant === 'luxury_strip') {
    return (
      <svg viewBox="0 0 400 60" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <rect x="0" y="0" width="400" height="60" fill={rfill} />
        <rect x="0" y="0" width="400" height="60" fill="url(#lstripSheen)" opacity="0.4" />
        <defs>
          <linearGradient id="lstripSheen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.35" />
          </linearGradient>
        </defs>
        <line x1="0" y1="2"  x2="400" y2="2"  stroke={strokeCol} strokeWidth="1" />
        <line x1="0" y1="58" x2="400" y2="58" stroke={strokeCol} strokeWidth="1" />
        <text x="200" y="40" fill={textCol} fontFamily={s.font || 'Cormorant Garamond'}
              fontWeight={s.weight || 700} fontSize="30"
              textAnchor="middle" letterSpacing="8" textRendering="geometricPrecision">{text}</text>
      </svg>
    );
  }
  if (variant === 'sash') {
    return (
      <svg viewBox="0 0 400 80" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <path d="M0,0 L400,0 L370,40 L400,80 L0,80 L30,40 Z" fill={rfill} stroke={strokeCol} />
        <text x="200" y="50" fill={textCol} fontFamily={s.font || 'Playfair Display'}
              fontWeight={s.weight || 800} fontSize="28" textAnchor="middle" letterSpacing="6">{text}</text>
      </svg>
    );
  }
  if (variant === 'pennant') {
    return (
      <svg viewBox="0 0 300 90" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}
        <polygon points="0,10 240,10 300,45 240,80 0,80" fill={rfill} stroke={strokeCol} />
        <text x="140" y="55" fill={textCol} fontFamily={s.font || 'Inter'}
              fontWeight={s.weight || 800} fontSize="26" textAnchor="middle" letterSpacing="4">{text}</text>
      </svg>
    );
  }
  // horizontal / default
  return (
    <svg viewBox="0 0 300 90" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
      {grad}
      <path d="M0,25 L280,25 L300,25 L280,65 L300,65 L0,65 Z" fill={rfill} stroke={strokeCol} />
      <text x="150" y="55" fill={textCol} fontFamily={s.font || 'Inter'}
            fontWeight={s.weight || 800} fontSize="26"
            textAnchor="middle" letterSpacing="4">{text}</text>
    </svg>
  );
}

// ── Bow — decorative gift-voucher bow (dual ribbon + centre knot) ─
function ElementBow({ el }) {
  const s = el.style || {};
  const fill      = s.fill  || '#D4A44A';
  const fill2     = s.fill2 || '#8B5E1E';
  const stroke    = s.stroke || 'rgba(0,0,0,0.30)';
  return (
    <svg viewBox="0 0 300 200" preserveAspectRatio="xMidYMid meet"
         style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={`bowg_${el.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={fill}  stopOpacity="1" />
          <stop offset="50%"  stopColor={fill}  stopOpacity="0.9" />
          <stop offset="100%" stopColor={fill2} stopOpacity="1" />
        </linearGradient>
        <linearGradient id={`bowsheen_${el.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {/* Ribbon tails */}
      <path d="M120,110 L60,190 L100,175 L130,120 Z"
            fill={`url(#bowg_${el.id})`} stroke={stroke} />
      <path d="M180,110 L240,190 L200,175 L170,120 Z"
            fill={`url(#bowg_${el.id})`} stroke={stroke} />
      {/* Left loop */}
      <path d="M150,80 C90,50 60,120 130,110 Z"
            fill={`url(#bowg_${el.id})`} stroke={stroke} />
      <path d="M150,80 C110,60 80,110 130,108"
            fill={`url(#bowsheen_${el.id})`} />
      {/* Right loop */}
      <path d="M150,80 C210,50 240,120 170,110 Z"
            fill={`url(#bowg_${el.id})`} stroke={stroke} />
      <path d="M150,80 C190,60 220,110 170,108"
            fill={`url(#bowsheen_${el.id})`} />
      {/* Centre knot */}
      <rect x="132" y="72" width="36" height="42" rx="6"
            fill={fill2} stroke={stroke} />
      <rect x="132" y="72" width="36" height="14" rx="3"
            fill={`url(#bowsheen_${el.id})`} />
    </svg>
  );
}

// ── Generic shape element with variants ────────────────────────
function ElementShape({ el }) {
  const s = el.style || {};
  const variant = s.variant || 'rectangle';
  const fill    = s.fill    || 'rgba(255,255,255,0.10)';
  const fill2   = s.fill2   || null;
  const stroke  = s.stroke  || 'transparent';
  const strokeWidth = s.stroke_width || 0;
  const radius  = s.border_radius || 0;
  const gid = `sgrad_${el.id}`;
  const F = fill2 ? `url(#${gid})` : fill;
  const grad = fill2 ? (
    <defs>
      <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor={fill} />
        <stop offset="100%" stopColor={fill2} />
      </linearGradient>
    </defs>
  ) : null;
  const common = { stroke, strokeWidth };
  if (variant === 'triangle') {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}<polygon points="50,4 96,96 4,96" fill={F} {...common} />
      </svg>
    );
  }
  if (variant === 'ellipse') {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}<ellipse cx="50" cy="50" rx="48" ry="48" fill={F} {...common} />
      </svg>
    );
  }
  if (variant === 'pill') {
    return (
      <div style={{ width: '100%', height: '100%', background: fill2 ? `linear-gradient(135deg, ${fill}, ${fill2})` : fill,
                    borderRadius: 9999, border: strokeWidth ? `${strokeWidth}px solid ${stroke}` : 'none' }} />
    );
  }
  if (variant === 'polygon') {
    const sides = Math.max(3, Math.min(12, s.sides || 6));
    const pts = Array.from({ length: sides }, (_, i) => {
      const a = (i / sides) * Math.PI * 2 - Math.PI / 2;
      return `${50 + 48 * Math.cos(a)},${50 + 48 * Math.sin(a)}`;
    }).join(' ');
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}<polygon points={pts} fill={F} {...common} />
      </svg>
    );
  }
  if (variant === 'diagonal_block') {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}<polygon points="0,100 100,0 100,100" fill={F} {...common} />
      </svg>
    );
  }
  if (variant === 'arc') {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}<path d="M0,100 Q50,0 100,100 Z" fill={F} {...common} />
      </svg>
    );
  }
  if (variant === 'wave') {
    return (
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {grad}<path d="M0,20 Q25,0 50,20 T100,20 L100,40 L0,40 Z" fill={F} {...common} />
      </svg>
    );
  }
  // rounded_rect / rectangle default
  return (
    <div style={{ width: '100%', height: '100%',
                  background: fill2 ? `linear-gradient(135deg, ${fill}, ${fill2})` : fill,
                  borderRadius: `${radius}px`,
                  border: strokeWidth ? `${strokeWidth}px solid ${stroke}` : 'none' }} />
  );
}

// ── Badges — shaped premium seals (star / shield / circle / starburst / pill) ─
function ElementBadge({ el }) {
  const s = el.style || {};
  const shape = s.shape || 'seal';
  const bg    = s.bg    || '#C9A227';
  const bg2   = s.bg2   || null;
  const text  = el.content?.text || 'VIP';
  const color = s.color || '#0E2C22';
  const stroke = s.stroke || 'rgba(0,0,0,0.35)';
  const gid = `bggrad_${el.id}`;
  const F = bg2 ? `url(#${gid})` : bg;
  if (shape === 'pill') {
    return (
      <div style={{ width: '100%', height: '100%',
                    background: bg2 ? `linear-gradient(135deg, ${bg}, ${bg2})` : bg,
                    color, fontFamily: s.font || 'Inter', fontWeight: s.weight || 800,
                    fontSize: `${(s.size || 0.024) * 100}cqw`,
                    letterSpacing: s.letter_spacing || '0.20em',
                    textTransform: s.text_transform || 'uppercase',
                    borderRadius: 9999, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 4px 12px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.28)` }}>
        {text}
      </div>
    );
  }
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet"
         style={{ width: '100%', height: '100%' }}>
      {bg2 && (
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={bg}  />
            <stop offset="100%" stopColor={bg2} />
          </linearGradient>
        </defs>
      )}
      {shape === 'seal' && (
        <polygon fill={F} stroke={stroke} strokeWidth="1"
                  points="50,4 60,15 74,10 78,25 92,30 87,44 96,55 84,64 88,78 74,80 66,92 52,86 40,94 30,82 16,82 15,66 5,58 12,45 4,32 18,28 22,14 36,16" />
      )}
      {shape === 'shield' && (
        <path fill={F} stroke={stroke} strokeWidth="1"
              d="M50,4 L92,20 L92,55 C92,80 70,94 50,96 C30,94 8,80 8,55 L8,20 Z" />
      )}
      {shape === 'star' && (
        <polygon fill={F} stroke={stroke} strokeWidth="1"
                  points="50,4 61,38 96,38 68,58 78,92 50,72 22,92 32,58 4,38 39,38" />
      )}
      {shape === 'starburst' && (
        <polygon fill={F} stroke={stroke} strokeWidth="1"
                  points="50,2 55,18 68,10 66,26 82,20 74,34 92,36 78,46 96,54 78,60 90,74 74,72 80,88 64,80 58,96 50,84 42,96 36,80 20,88 26,72 10,74 22,60 4,54 22,46 8,36 26,34 18,20 34,26 32,10 45,18" />
      )}
      {shape === 'circle' && (
        <circle cx="50" cy="50" r="46" fill={F} stroke={stroke} strokeWidth="1" />
      )}
      <text x="50" y="55" fill={color} fontFamily={s.font || 'Inter'}
            fontWeight={s.weight || 800} fontSize="18" textAnchor="middle"
            letterSpacing="1">{text}</text>
    </svg>
  );
}

// ── Icons — resolves against lucide-react via the parent registry ─
function ElementIcon({ el }) {
  const s = el.style || {};
  const name = el.content?.name || 'Star';
  const col  = s.color || '#C9A227';
  const Icon = LucideAll[name] || LucideAll.Star;
  return (
    <div style={{ width: '100%', height: '100%', color: col,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon style={{ width: '100%', height: '100%' }} strokeWidth={s.stroke_width || 1.5} />
    </div>
  );
}

// ── Effect — visible premium finishing touches ─────────────────
function ElementEffect({ el }) {
  const s = el.style || {};
  const variant = s.variant || 'glow';
  const col = s.color || 'rgba(255,215,120,0.55)';
  const col2 = s.color2 || 'rgba(255,255,255,0.35)';
  const opacity = s.opacity ?? 1;
  if (variant === 'shimmer') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden',
                    background: `linear-gradient(120deg,
                                  transparent 20%,
                                  rgba(255,255,255,0.28) 45%,
                                  rgba(255,255,255,0.72) 50%,
                                  rgba(255,255,255,0.28) 55%,
                                  transparent 80%)`,
                    mixBlendMode: 'overlay',
                    animation: 'voucherShimmerLive 5s ease-in-out infinite',
                    opacity }} />
    );
  }
  if (variant === 'shine_strip') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', opacity,
                    background: `linear-gradient(115deg,
                                  transparent 0%,
                                  transparent 35%,
                                  ${col} 47%,
                                  #ffffff 50%,
                                  ${col} 53%,
                                  transparent 65%,
                                  transparent 100%)`,
                    mixBlendMode: 'screen' }} />
    );
  }
  if (variant === 'shadow') {
    return (
      <div style={{ width: '100%', height: '100%',
                    boxShadow: `inset 0 0 60px rgba(0,0,0,${opacity * 0.75})`,
                    pointerEvents: 'none' }} />
    );
  }
  if (variant === 'glass') {
    return (
      <div style={{ width: '100%', height: '100%',
                    background: 'rgba(255,255,255,0.14)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.28)',
                    borderRadius: `${s.border_radius || 12}px`,
                    opacity }} />
    );
  }
  if (variant === 'highlight') {
    return (
      <div style={{ width: '100%', height: '100%',
                    background: `linear-gradient(180deg, ${col} 0%, transparent 55%)`,
                    borderRadius: `${s.border_radius || 8}px`, opacity }} />
    );
  }
  if (variant === 'blur_blob') {
    return (
      <div style={{ width: '100%', height: '100%', borderRadius: '50%',
                    background: `radial-gradient(circle, ${col} 0%, transparent 65%)`,
                    filter: 'blur(24px)', opacity }} />
    );
  }
  if (variant === 'gradient_glow') {
    return (
      <div style={{ width: '100%', height: '100%', borderRadius: '50%',
                    background: `radial-gradient(ellipse, ${col} 0%, ${col2} 45%, transparent 75%)`,
                    filter: 'blur(6px)', opacity }} />
    );
  }
  if (variant === 'metallic') {
    return (
      <div style={{ width: '100%', height: '100%',
                    background: `linear-gradient(135deg,
                                  ${col} 0%,
                                  #fff8d6 30%,
                                  ${col} 55%,
                                  ${col2} 78%,
                                  ${col} 100%)`,
                    mixBlendMode: 'overlay', opacity,
                    borderRadius: `${s.border_radius || 4}px` }} />
    );
  }
  // 'glow' → soft radial highlight
  return (
    <div style={{ width: '100%', height: '100%', borderRadius: '50%',
                  background: `radial-gradient(circle, ${col} 0%, transparent 70%)`, opacity }} />
  );
}
function ElementBrand({ el, ctx }) {
  const s = el.style || {};
  const name = ctx.outlet?.name || '';
  const tagline = ctx.outlet?.tagline || '';
  const showTag = el.content?.show_tagline !== false;
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: s.align === 'right' ? 'flex-end' :
                              s.align === 'center' ? 'center' : 'flex-start' }}>
      <div style={{ color: s.color || '#F7F5EE',
                    fontFamily: s.font || 'Montserrat',
                    fontWeight: s.weight || 700,
                    fontSize: `${(s.size || 0.038) * 100}cqw`,
                    letterSpacing: s.letter_spacing || '0.06em',
                    textTransform: s.text_transform || 'uppercase' }}>
        {name}
      </div>
      {showTag && tagline && (
        <div style={{ color: s.color || 'rgba(247,245,238,0.72)',
                      fontFamily: 'Inter', fontWeight: 400,
                      fontSize: `${((s.size || 0.038) * 0.55) * 100}cqw`,
                      opacity: 0.72, marginTop: '0.5cqw' }}>
          {tagline}
        </div>
      )}
    </div>
  );
}

const _TYPE_RENDER = {
  brand:     ElementBrand,
  logo:      ElementLogo,
  title:     (p) => <ElementText {...p} text={resolveContent(p.el, p.ctx)} />,
  subtitle:  (p) => <ElementText {...p} text={resolveContent(p.el, p.ctx)} />,
  value:     (p) => <ElementText {...p} text={resolveContent(p.el, p.ctx)} />,
  qr:        ElementQR,
  code:      (p) => <ElementText {...p} text={resolveContent(p.el, p.ctx)} />,
  validity:  (p) => <ElementText {...p} text={resolveContent(p.el, p.ctx)} />,
  terms_cta: (p) => <ElementText {...p} text={resolveContent(p.el, p.ctx)} />,
  text:      (p) => <ElementText {...p} text={p.el.content?.text || ''} />,
  rectangle: ElementRect,
  circle:    ElementCircle,
  line:      ElementLine,
  divider:   ElementLine,
  image:     ElementImage,
  chip:      ElementChip,
  ribbon:    ElementRibbon,
  badge:     ElementBadge,
  icon:      ElementIcon,
  effect:    ElementEffect,
  bow:       ElementBow,
  shape:     ElementShape,
};

// ── Canvas render ──────────────────────────────────────────────
// A canvas background can be:
//   • a string  ← direct CSS value (single colour / gradient / url())
//   • an object ← { type: solid | gradient | radial | image | split,
//                    value, angle, stops[], overlay: {type,color,opacity},
//                    fit, position, opacity, blur, ... }
// Split composition supports named layouts (50_50, 30_70, vertical,
// horizontal, diagonal, corner_block, band_top/bottom/left/right).
// Everything else must fall through to a safe default so legacy docs
// created before the richer spec still render.
function renderCanvasBackground(canvas) {
  const bg = canvas?.background;
  if (!bg) return { background: '#111' };
  if (typeof bg === 'string') return { background: bg };
  const style = {};
  if (bg.type === 'solid')      style.background = bg.value;
  else if (bg.type === 'gradient' || bg.type === 'linear') {
    // stops[] takes precedence over a raw `value` string.
    if (Array.isArray(bg.stops) && bg.stops.length) {
      const angle = bg.angle ?? 135;
      style.background = `linear-gradient(${angle}deg, ${bg.stops.map(s => `${s.color} ${(s.pos ?? '')}${s.pos == null ? '' : '%'}`).join(', ')})`;
    } else {
      style.background = bg.value || `linear-gradient(135deg, #101013 0%, #202024 100%)`;
    }
  } else if (bg.type === 'radial') {
    if (Array.isArray(bg.stops) && bg.stops.length) {
      style.background = `radial-gradient(circle at ${bg.origin || 'center'}, ${bg.stops.map(s => `${s.color} ${(s.pos ?? '')}${s.pos == null ? '' : '%'}`).join(', ')})`;
    } else {
      style.background = bg.value || `radial-gradient(circle, #202024 0%, #101013 100%)`;
    }
  } else if (bg.type === 'image')    {
    const fit = bg.fit || 'cover';
    const pos = bg.position || 'center';
    const url = bg.value || '';
    style.background = `url(${url}) ${pos}/${fit} no-repeat`;
    if (bg.opacity != null && bg.opacity < 1) {
      // opacity handled via overlay in canvas render (not here to keep it simple).
    }
  }
  else if (bg.type === 'split') {
    // Split composition — two colours in a named layout.
    const c1 = bg.color1 || '#0E2C22';
    const c2 = bg.color2 || '#D4A44A';
    const layout = bg.layout || '50_50';
    if (layout === '50_50' || layout === 'vertical') {
      style.background = `linear-gradient(90deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`;
    } else if (layout === '30_70') {
      style.background = `linear-gradient(90deg, ${c1} 0%, ${c1} 30%, ${c2} 30%, ${c2} 100%)`;
    } else if (layout === '70_30') {
      style.background = `linear-gradient(90deg, ${c1} 0%, ${c1} 70%, ${c2} 70%, ${c2} 100%)`;
    } else if (layout === 'horizontal') {
      style.background = `linear-gradient(180deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`;
    } else if (layout === 'diagonal') {
      style.background = `linear-gradient(135deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`;
    } else if (layout === 'diagonal_reverse') {
      style.background = `linear-gradient(45deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`;
    } else if (layout === 'corner_block') {
      style.background = `${c1} linear-gradient(135deg, transparent 50%, ${c2} 50%)`;
    } else if (layout === 'band_top') {
      style.background = `linear-gradient(180deg, ${c2} 0%, ${c2} 20%, ${c1} 20%, ${c1} 100%)`;
    } else if (layout === 'band_bottom') {
      style.background = `linear-gradient(180deg, ${c1} 0%, ${c1} 80%, ${c2} 80%, ${c2} 100%)`;
    } else if (layout === 'band_left') {
      style.background = `linear-gradient(90deg, ${c2} 0%, ${c2} 15%, ${c1} 15%, ${c1} 100%)`;
    } else if (layout === 'band_right') {
      style.background = `linear-gradient(90deg, ${c1} 0%, ${c1} 85%, ${c2} 85%, ${c2} 100%)`;
    } else {
      style.background = `linear-gradient(90deg, ${c1} 0%, ${c2} 100%)`;
    }
  }
  else style.background = bg.value || '#111';
  return style;
}

// Pattern overlays — SVG data URIs so no external assets required.
const _PATTERN_SVGS = {
  none:  '',
  dots:  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><circle cx="4" cy="4" r="1.2" fill="COLOR"/></svg>',
  lines: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><path d="M0 14 L14 0" stroke="COLOR" stroke-width="1" fill="none"/></svg>',
  grid:  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><path d="M32 0 L0 0 0 32" stroke="COLOR" stroke-width="0.6" fill="none"/></svg>',
  crosshatch: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><path d="M0 14 L14 0 M0 0 L14 14" stroke="COLOR" stroke-width="0.7" fill="none"/></svg>',
  waves: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="18" preserveAspectRatio="none"><path d="M0 9 Q 15 0 30 9 T 60 9" stroke="COLOR" fill="none" stroke-width="1"/></svg>',
  noise: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="180" height="180"><filter id="n"><feTurbulence baseFrequency="0.9" numOctaves="3"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.55" fill="COLOR"/></svg>',
  luxury: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><g fill="none" stroke="COLOR" stroke-width="0.8"><circle cx="24" cy="24" r="10"/><path d="M4 24 h40 M24 4 v40"/></g></svg>',
  damask: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72"><g fill="none" stroke="COLOR" stroke-width="0.9"><path d="M36 6 C 20 20 20 40 36 66 C 52 40 52 20 36 6 Z"/><circle cx="36" cy="18" r="3"/><circle cx="36" cy="54" r="3"/><path d="M6 36 C 20 20 40 20 66 36 C 40 52 20 52 6 36 Z"/></g></svg>',
  ornamental: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60"><g fill="none" stroke="COLOR" stroke-width="0.9"><path d="M30 4 L30 56 M4 30 L56 30 M12 12 L48 48 M48 12 L12 48"/><circle cx="30" cy="30" r="8"/><circle cx="30" cy="30" r="14"/></g></svg>',
  filigree: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="84" height="84"><g fill="none" stroke="COLOR" stroke-width="0.8"><path d="M42 8 Q60 24 42 42 Q24 24 42 8"/><path d="M42 42 Q60 60 42 76 Q24 60 42 42"/><path d="M8 42 Q24 24 42 42 Q24 60 8 42"/><path d="M42 42 Q60 24 76 42 Q60 60 42 42"/></g></svg>',
  stars: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><polygon points="20,4 24,16 36,16 26,24 30,36 20,28 10,36 14,24 4,16 16,16" fill="COLOR" opacity="0.7"/></svg>',
};
function CanvasPatternOverlay({ pattern, color = 'rgba(255,255,255,0.08)', opacity = 0.5 }) {
  if (!pattern || pattern === 'none') return null;
  const svg = _PATTERN_SVGS[pattern];
  if (!svg) return null;
  const uri = svg.replace(/COLOR/g, encodeURIComponent(color));
  return (
    <div aria-hidden="true"
         style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: `url("${uri}")`,
                  opacity, zIndex: 1 }} />
  );
}

// Clamp an element's box so critical elements can't leave the canvas.
export function clampElement(el, delta = {}) {
  const isQR = el.id === 'qr' || el.type === 'qr';
  let { x, y, width, height, rotation } = { ...el, ...delta };
  // Size floors — kept generous to allow small decorative accents.
  const minW = isQR ? MIN_QR_WIDTH : MIN_ELEMENT_WIDTH;
  width  = Math.max(minW, Math.min(1, width));
  height = Math.max(MIN_ELEMENT_HEIGHT, Math.min(1, height));
  // Position clamps — allow slight bleed on ALL elements now that
  // there are no forced-in-view functional bindings.
  x = Math.max(-width * 0.5, Math.min(1 - width * 0.5, x));
  y = Math.max(-height * 0.5, Math.min(1 - height * 0.5, y));
  return { ...el, x, y, width, height, rotation };
}

// ══════════════════════════════════════════════════════════════
//                        RENDERER
// ══════════════════════════════════════════════════════════════
export default function VoucherDesignRenderer({
  design,
  voucher,
  outlet,                 // { name, tagline, logo, initials }
  qrValue,
  validUntil,
  revealed = false,       // Phase 2B · true when guest has tapped Reveal
  revealedCode = null,    // Phase 2B · plaintext code exposed only when revealed
  mode = 'read',          // 'read' | 'edit'
  size = 'phone',         // 'phone' | 'full' | 'thumb'
  className = '',
  // Edit-mode extras (safely ignored in read mode).
  selectedId = null,
  onSelect,
  onMutate,
  onCanvasClick,
  showGuides = true,
}) {
  const canvas = design?.canvas || {};
  // Recovery-Phase-0 · safe defaults so a malformed payload never
  // collapses the card to 0-height (production Guest Wallet symptom).
  const _rawAspect = Number(canvas.aspect_ratio);
  const aspect = Number.isFinite(_rawAspect) && _rawAspect > 0 ? _rawAspect : 1.586;
  const radius = canvas.border?.radius ?? 20;
  const border = canvas.border ? `${canvas.border.width || 1}px solid ${canvas.border.color || 'rgba(255,255,255,0.15)'}` : 'none';
  const shadow = canvas.shadow || '0 22px 60px -18px rgba(0,0,0,0.55)';
  const bgStyle = renderCanvasBackground(canvas);

  const wrapperWidth = size === 'thumb' ? 'max-w-[220px]'
                     : size === 'phone' ? (aspect >= 1.85 ? 'max-w-[520px]' : 'max-w-[380px]')
                     : (aspect >= 1.85 ? 'max-w-[720px]' : 'max-w-[560px]');

  const els = (design?.elements || []).slice().sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
  const ctx = { voucher, outlet, qrValue, validUntil, revealed, revealedCode };

  return (
    <div
      className={`relative w-full ${wrapperWidth} ${className}`}
      style={{
        aspectRatio: `${aspect}`,
        containerType: 'inline-size',
        borderRadius: `${radius}px`,
        overflow: 'hidden',
        boxShadow: shadow,
        border,
        ...bgStyle,
      }}
      data-testid="voucher-design-renderer"
      data-design-version={design?.design_version || 1}
      onMouseDown={(e) => { if (mode === 'edit' && e.target === e.currentTarget) onCanvasClick?.(); }}
    >
      {/* Canvas-level pattern overlay */}
      <CanvasPatternOverlay
        pattern={canvas?.pattern?.key}
        color={canvas?.pattern?.color || 'rgba(255,255,255,0.10)'}
        opacity={canvas?.pattern?.opacity ?? 0.55}
      />
      {/* Shimmer animation keyframes — injected once. */}
      <style>{`
        @keyframes voucherShimmerLive {
          0%   { transform: translateX(-30%); opacity: 0.4; }
          50%  { opacity: 1; }
          100% { transform: translateX(30%); opacity: 0.4; }
        }
      `}</style>
      {els.map((el) => {
        if (el.hidden) return null;
        // Recovery-Phase-0 · silently skip unknown types rather than
        // rendering an empty <text> box. Unknown types are usually
        // legacy element names or admin typos — hiding them keeps the
        // rest of the card intact.
        const Cmp = _TYPE_RENDER[el.type];
        if (!Cmp) return null;
        const box = elementBoxStyle(el, canvas);
        const isSelected = mode === 'edit' && selectedId === el.id;
        return (
          <div
            key={el.id}
            data-el-id={el.id}
            data-el-type={el.type}
            style={{
              ...box,
              cursor: mode === 'edit' ? (el.locked ? 'not-allowed' : 'move') : 'default',
              outline: isSelected ? '2px solid #C9A227' : 'none',
              outlineOffset: isSelected ? '2px' : 0,
            }}
            onMouseDown={mode === 'edit' ? (e) => {
              e.stopPropagation();
              onSelect?.(el.id);
              if (el.locked) return;
              startDrag(e, el, onMutate);
            } : undefined}
          >
            <Cmp el={el} ctx={ctx} />
            {isSelected && !el.locked && (
              <SelectionHandles el={el} onMutate={onMutate} />
            )}
          </div>
        );
      })}
      {/* Alignment guides during a drag (edit only) */}
      {mode === 'edit' && showGuides && <AlignmentGuidesHost />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//                     EDIT-MODE INTERACTIONS
// ══════════════════════════════════════════════════════════════

// Container-relative math: derive %-delta from a mouse-move event.
function relativeDelta(startEvt, currentEvt, container) {
  const rect = container.getBoundingClientRect();
  return {
    dx: (currentEvt.clientX - startEvt.clientX) / rect.width,
    dy: (currentEvt.clientY - startEvt.clientY) / rect.height,
  };
}

// Root container for the currently-active pointer session — used both
// for drag/resize and for painting alignment guides on top of the card.
let _ACTIVE_CONTAINER = null;
let _ACTIVE_GUIDES = { vertical: null, horizontal: null };

function paintGuides(elBox) {
  if (!_ACTIVE_CONTAINER) return;
  const rect = _ACTIVE_CONTAINER.getBoundingClientRect();
  const cx = elBox.x + elBox.width / 2;
  const cy = elBox.y + elBox.height / 2;
  const vertical = Math.abs(cx - 0.5) < 0.01 ? 0.5
                 : Math.abs(elBox.x) < 0.01 ? 0
                 : Math.abs(elBox.x + elBox.width - 1) < 0.01 ? 1
                 : null;
  const horizontal = Math.abs(cy - 0.5) < 0.01 ? 0.5
                   : Math.abs(elBox.y) < 0.01 ? 0
                   : Math.abs(elBox.y + elBox.height - 1) < 0.01 ? 1
                   : null;
  window.dispatchEvent(new CustomEvent('voucher-guides', {
    detail: { vertical, horizontal, rect },
  }));
}
function clearGuides() {
  window.dispatchEvent(new CustomEvent('voucher-guides', { detail: { vertical: null, horizontal: null } }));
}

function startDrag(e, el, onMutate) {
  const container = e.currentTarget.parentElement;
  _ACTIVE_CONTAINER = container;
  const startEl = { ...el };
  const startEvt = e;
  const onMove = (ev) => {
    const { dx, dy } = relativeDelta(startEvt, ev, container);
    // Snap-to-centre / edges when within 1 % of the target.
    let nx = startEl.x + dx;
    let ny = startEl.y + dy;
    const cxTarget = 0.5 - startEl.width / 2;
    const cyTarget = 0.5 - startEl.height / 2;
    if (Math.abs(nx - cxTarget) < 0.012) nx = cxTarget;
    if (Math.abs(ny - cyTarget) < 0.012) ny = cyTarget;
    if (Math.abs(nx) < 0.012) nx = 0;
    if (Math.abs(nx + startEl.width - 1) < 0.012) nx = 1 - startEl.width;
    const patched = clampElement(startEl, { x: nx, y: ny });
    onMutate?.(startEl.id, { x: patched.x, y: patched.y });
    paintGuides(patched);
  };
  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    clearGuides();
    _ACTIVE_CONTAINER = null;
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
}

// 8 resize handles + 1 rotate knob.
const RESIZE_HANDLES = [
  { key: 'nw', cursor: 'nwse-resize', x: 0,   y: 0   },
  { key: 'n',  cursor: 'ns-resize',   x: 0.5, y: 0   },
  { key: 'ne', cursor: 'nesw-resize', x: 1,   y: 0   },
  { key: 'e',  cursor: 'ew-resize',   x: 1,   y: 0.5 },
  { key: 'se', cursor: 'nwse-resize', x: 1,   y: 1   },
  { key: 's',  cursor: 'ns-resize',   x: 0.5, y: 1   },
  { key: 'sw', cursor: 'nesw-resize', x: 0,   y: 1   },
  { key: 'w',  cursor: 'ew-resize',   x: 0,   y: 0.5 },
];

function SelectionHandles({ el, onMutate }) {
  const startResize = (handle) => (e) => {
    e.stopPropagation();
    const container = e.currentTarget.closest('[data-testid="voucher-design-renderer"]');
    if (!container) return;
    _ACTIVE_CONTAINER = container;
    const startEl = { ...el };
    const startEvt = e;
    const onMove = (ev) => {
      const { dx, dy } = relativeDelta(startEvt, ev, container);
      let nx = startEl.x, ny = startEl.y, nw = startEl.width, nh = startEl.height;
      if (handle.includes('e')) nw = startEl.width + dx;
      if (handle.includes('w')) { nw = startEl.width - dx; nx = startEl.x + dx; }
      if (handle.includes('s')) nh = startEl.height + dy;
      if (handle.includes('n')) { nh = startEl.height - dy; ny = startEl.y + dy; }
      const patched = clampElement(startEl, { x: nx, y: ny, width: nw, height: nh });
      onMutate?.(startEl.id, {
        x: patched.x, y: patched.y, width: patched.width, height: patched.height,
      });
      paintGuides(patched);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      clearGuides();
      _ACTIVE_CONTAINER = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startRotate = (e) => {
    e.stopPropagation();
    const box = e.currentTarget.closest('[data-el-id]');
    const r = box.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const start = el.rotation || 0;
    const a0 = Math.atan2(e.clientY - cy, e.clientX - cx);
    const onMove = (ev) => {
      const a1 = Math.atan2(ev.clientY - cy, ev.clientX - cx);
      const deg = start + ((a1 - a0) * 180 / Math.PI);
      onMutate?.(el.id, { rotation: Math.round(deg) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <>
      {RESIZE_HANDLES.map(h => (
        <div key={h.key}
             data-handle={h.key}
             onMouseDown={startResize(h.key)}
             style={{
               position: 'absolute', left: `${h.x * 100}%`, top: `${h.y * 100}%`,
               width: 10, height: 10, marginLeft: -5, marginTop: -5,
               background: '#C9A227', borderRadius: 2, cursor: h.cursor,
               boxShadow: '0 1px 3px rgba(0,0,0,0.35)', zIndex: 999,
             }}
        />
      ))}
      <div onMouseDown={startRotate}
           title="Rotate"
           style={{ position: 'absolute', left: '50%', top: -18, marginLeft: -6,
                    width: 12, height: 12, background: '#C9A227',
                    borderRadius: '50%', cursor: 'grab',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.35)', zIndex: 999 }} />
    </>
  );
}

// Live alignment guides painted absolutely INSIDE the renderer.
function AlignmentGuidesHost() {
  const [state, setState] = useState({ vertical: null, horizontal: null });
  React.useEffect(() => {
    const handler = (e) => setState(e.detail || { vertical: null, horizontal: null });
    window.addEventListener('voucher-guides', handler);
    return () => window.removeEventListener('voucher-guides', handler);
  }, []);
  return (
    <>
      {state.vertical !== null && (
        <div style={{ position: 'absolute', top: 0, bottom: 0,
                      left: `${state.vertical * 100}%`,
                      width: 1, background: '#F0C64C',
                      pointerEvents: 'none', zIndex: 1000 }} />
      )}
      {state.horizontal !== null && (
        <div style={{ position: 'absolute', left: 0, right: 0,
                      top: `${state.horizontal * 100}%`,
                      height: 1, background: '#F0C64C',
                      pointerEvents: 'none', zIndex: 1000 }} />
      )}
    </>
  );
}
